import cn from 'classnames';
import { useRouter } from 'next/router';
import type { MouseEventHandler } from 'react';
import React, { memo, useCallback } from 'react';

import { Button } from '@sravni/react-design-system';
import type { ButtonProps } from '@sravni/react-design-system/dist/types/components/Button';

import type { AffSub4 } from '@src/@types/microcredits';
import { PIXELS_TYPES } from '@src/constants/analytics';
import { MONETIZATION_INTERNAL } from '@src/helpers/advertising';
import type { IAnalyticsItem } from '@src/helpers/analyticsEvents';
import { sendAdvertisingButtonClickEvent } from '@src/helpers/analyticsEvents';
import { open } from '@src/helpers/navigationHelper';
import { usePixel } from '@src/hooks/usePixel';
import { isSpecialPageWithoutQuery } from '@src/utils/routing';

import styles from './styles.module.scss';

interface IProps extends ButtonProps {
    className?: string;
    advSub?: string;
    source?: string;
    analytics: IAnalyticsItem;
    productLinkHref?: string;
    onClick?: () => void;
    affSub4?: AffSub4;
}

export const AdvertisingButton: React.FunctionComponent<IProps> = memo(
    ({ className, source, analytics, advSub, productLinkHref, onClick, affSub4, ...buttonProps }) => {
        const router = useRouter();
        const { asPath } = router;
        const isSpecial = isSpecialPageWithoutQuery(asPath);
        const link = analytics.advertising?.monetization?.pixels?.click;
        const monetizationKind = analytics.advertising?.monetization?.kind;

        const href = usePixel(link, {
            sub: advSub,
            affSub4,
            type: analytics.advertising?.productType,
            source,
            pixelType: PIXELS_TYPES.CLICK,
        });

        const handleClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
            (event) => {
                event.stopPropagation();
                event.preventDefault();
                sendAdvertisingButtonClickEvent(analytics);

                onClick?.();

                if (buttonProps.variant === 'secondary' && productLinkHref) {
                    router.push(productLinkHref);
                    return;
                }

                if (!href) {
                    return;
                }

                if (monetizationKind === MONETIZATION_INTERNAL) {
                    router.push(href);
                    return;
                }

                if (href) {
                    open({
                        href,
                        target: '_blank',
                        features: 'noopener',
                    });
                }
            },
            [analytics, onClick, buttonProps.variant, productLinkHref, href, monetizationKind, router],
        );

        return (
            <a className={cn(styles.link, className)} onClick={handleClick}>
                <Button className={isSpecial ? styles.button : styles.buttonFake} variant="primary" {...buttonProps}>
                    Получить деньги
                </Button>
            </a>
        );
    },
);
